import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Autocomplete,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Refresh,
  Print,
  Download,
  Business,
  LocalShipping as TruckIcon,
  Warning as WarningIcon,
  CheckCircle,
  Visibility,
  VisibilityOff,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import {
  issueList,
  issueSolveapi,
  partyList,
} from "../../../Services/Services";
import IssueForm from "./IssueForm";
import { T } from "../../../theme";

// ── Shared Styles ──────────────────────────────────────────────────
const sq = { borderRadius: "4px !important" };
const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};
const successBtn = {
  ...sq,
  border: `1.5px solid ${T.successBorder}`,
  bgcolor: T.successBg,
  color: T.success,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#d1fae5", borderColor: T.success, boxShadow: "none" },
};
const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };
const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.white },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

// ── Dialog Header ──────────────────────────────────────────────────
const DialogHeader = ({ title, onClose }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        color: T.white,
        fontSize: "0.88rem",
        letterSpacing: "0.01em",
      }}
    >
      {title}
    </Typography>
    <IconButton
      onClick={onClose}
      size="small"
      sx={{
        color: "rgba(255,255,255,0.8)",
        p: 0.5,
        ...sq,
        "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
      }}
    >
      <CloseIcon sx={{ fontSize: 17 }} />
    </IconButton>
  </Box>
);

// ── SolvePopover ───────────────────────────────────────────────────
const SolvePopover = ({ anchorEl, onClose, onConfirm }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 240 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: T.successBg,
            border: `1.5px solid ${T.successBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckCircle sx={{ fontSize: 20, color: T.success }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Mark as Solved?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        Are you sure you want to mark this issue as solved?
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{ ...successBtn, height: 30, fontSize: "0.7rem" }}
        >
          Mark Solved
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── MobileIssueCard ────────────────────────────────────────────────
const MobileIssueCard = ({ issue, idx, expanded, onToggle, onSolve }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 1,
      ...sq,
      border: `1.5px solid ${expanded ? T.primary : T.border}`,
      bgcolor: T.white,
      overflow: "hidden",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
      <Box
        sx={{
          width: 60,
          flexShrink: 0,
          bgcolor: expanded ? T.primary : T.warningBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
          transition: "background-color 0.2s",
          gap: 0.25,
        }}
      >
        <WarningIcon
          sx={{ fontSize: 20, color: expanded ? T.white : T.warning }}
        />
        <Typography
          sx={{
            fontSize: "0.52rem",
            fontWeight: 700,
            color: expanded ? T.white : T.warning,
          }}
        >
          #{idx + 1}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: 1.5,
          py: 1,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.82rem",
            color: T.text,
            lineHeight: 1.2,
            mb: 0.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {issue.truck_no || "—"}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: T.textSub,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mb: 0.25,
          }}
        >
          {issue.party_name || "—"}
        </Typography>
        <Chip
          label={`LR #${issue.freight_id || "—"}`}
          size="small"
          sx={{
            width: "fit-content",
            ...sq,
            bgcolor: T.white,
            color: T.primary,
            border: `1px solid ${T.border}`,
            fontSize: "0.55rem",
            height: 16,
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-end",
          px: 1.2,
          py: 1,
          minWidth: 90,
        }}
      >
        <Chip
          label="Pending"
          size="small"
          sx={{
            ...sq,
            bgcolor: T.warningBg,
            color: T.warning,
            border: `1px solid ${T.warningBorder}`,
            fontSize: "0.55rem",
            height: 18,
          }}
        />
        <Button
          size="small"
          onClick={onToggle}
          startIcon={
            expanded ? (
              <VisibilityOff sx={{ fontSize: 12 }} />
            ) : (
              <Visibility sx={{ fontSize: 12 }} />
            )
          }
          sx={{
            ...ghostBtn,
            height: 26,
            fontSize: "0.62rem",
            px: 1,
            "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
          }}
        >
          {expanded ? "Hide" : "Details"}
        </Button>
      </Box>
    </Box>
    {expanded && (
      <Box
        sx={{ bgcolor: T.white, borderTop: `1.5px solid ${T.border}`, p: 1.5 }}
      >
        <Stack spacing={1.25}>
          <Box>
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: T.primary,
                mb: 0.5,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              📋 Basic Info
            </Typography>
            <Grid container spacing={0.5}>
              {[
                ["LR ID", issue.freight_id],
                ["Issue ID", issue.issue_id],
                [
                  "Recovery Amt",
                  issue.recovery_amount
                    ? formatCurrency(issue.recovery_amount)
                    : "—",
                ],
                ["Party", issue.party_name, 12],
              ].map(([l, v, xs = 6]) => (
                <Grid item xs={xs} key={l}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {l}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: l === "Recovery Amt" ? T.success : T.text,
                    }}
                  >
                    {v || "—"}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box
            sx={{
              p: 1,
              bgcolor: T.white,
              border: `1px solid ${T.border}`,
              ...sq,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: T.primary,
                mb: 0.5,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              🚛 Vehicle
            </Typography>
            <Grid container spacing={0.5}>
              {[
                ["Truck No", issue.truck_no],
                ["Owner", issue.truck_owner],
              ].map(([l, v]) => (
                <Grid item xs={6} key={l}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {l}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.7rem", fontWeight: 600, color: T.text }}
                  >
                    {v || "—"}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
          {issue.description && (
            <Box
              sx={{
                p: 1,
                bgcolor: T.warningBg,
                border: `1px solid ${T.warningBorder}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.warning,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                📝 Description
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", color: T.text, fontWeight: 500 }}
              >
                {issue.description}
              </Typography>
            </Box>
          )}
        </Stack>
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mt: 1.5,
            pt: 1.25,
            borderTop: `1px dashed ${T.border}`,
            justifyContent: "flex-end",
          }}
        >
          <Button
            size="small"
            startIcon={<CheckCircle sx={{ fontSize: 13 }} />}
            onClick={(e) => onSolve(e, issue.issue_id)}
            sx={{ ...successBtn, height: 30, px: 1.5 }}
          >
            Mark as Solved
          </Button>
        </Box>
      </Box>
    )}
  </Paper>
);

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, subtext, color, icon: Icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.25,
      ...sq,
      position: "relative",
      overflow: "hidden",
      bgcolor: T.white,
      border: `1.5px solid ${color}30`,
      boxShadow: `0 1px 6px ${color}12`,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 6px 18px ${color}22`,
        borderColor: `${color}60`,
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "2.5px",
        background: `linear-gradient(90deg, ${color}cc, ${color}44)`,
        borderRadius: "4px 4px 0 0",
      },
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: -14,
        right: -14,
        width: 48,
        height: 48,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${color}14 0%, transparent 70%)`,
        pointerEvents: "none",
      },
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "8px",
        background: `linear-gradient(135deg, ${color}22 0%, ${color}10 100%)`,
        border: `1px solid ${color}25`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: `0 2px 6px ${color}15`,
        zIndex: 1,
      }}
    >
      <Icon sx={{ fontSize: 17, color }} />
    </Box>
    <Box sx={{ zIndex: 1, minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: "0.6rem",
          color: T.textSub,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          lineHeight: 1,
          mb: 0.3,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "1rem",
          fontWeight: 800,
          color: T.text,
          lineHeight: 1.1,
          letterSpacing: "-0.01em",
        }}
      >
        {value ?? 0}
      </Typography>
      {subtext && (
        <Typography
          sx={{
            fontSize: "0.58rem",
            color: T.textMuted,
            fontWeight: 600,
            mt: 0.25,
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {subtext}
        </Typography>
      )}
    </Box>
  </Paper>
);

// ── Main Component ─────────────────────────────────────────────────
export default function IssueList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [issues, setIssues] = useState([]);
  const [totalIssuesCount, setTotalIssuesCount] = useState(0); // ← unfiltered total from first load
  const [openAddModal, setOpenAddModal] = useState(false);
  const [solveAnchorEl, setSolveAnchorEl] = useState(null);
  const [issueToSolve, setIssueToSolve] = useState(null);
  const [partyOptions, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);

  // ── Search state — stores raw input values ─────────────────────
  const [search, setSearch] = useState({
    vehicle_no: "",
    party_name: "",
    truck_owner: "",
  });

  // ── Debounce ref — avoid firing API on every keystroke ─────────
  const debounceRef = useRef(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  // ── Fetch parties once ─────────────────────────────────────────
  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setParties(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setParties([]);
      }
    };
    fetchParties();
  }, []);

  // ── Core fetch — accepts filter params ─────────────────────────
  const fetchIssues = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const data = await issueList({
        truck_no: filters.vehicle_no || "",
        party_name: filters.party_name || "",
        truck_owner: filters.truck_owner || "",
      });
      setIssues(data || []);

      // Track the unfiltered total only when no filters are active
      const hasFilters =
        filters.vehicle_no || filters.party_name || filters.truck_owner;
      if (!hasFilters) {
        setTotalIssuesCount(data?.length || 0);
      }
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: "Failed to load issues",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Initial load ───────────────────────────────────────────────
  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  // ── Debounced search — fires 450ms after user stops typing ─────
  useEffect(() => {
    // Skip the very first render (all empty)
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchIssues(search);
    }, 450);

    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // ── Mark issue solved ──────────────────────────────────────────
  const handleSolve = async (id) => {
    try {
      await issueSolveapi(id);
      // Re-fetch with current filters so list stays consistent
      await fetchIssues(search);
      setSnackbar({
        open: true,
        message: "Issue marked as solved successfully!",
        severity: "success",
      });
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: "Failed to mark issue as solved",
        severity: "error",
      });
    }
    setSolveAnchorEl(null);
    setIssueToSolve(null);
  };

  // ── Clear all filters ──────────────────────────────────────────
  const clearFilters = () => {
    setSearch({ vehicle_no: "", party_name: "", truck_owner: "" });
    setExpandedCardId(null);
    // useEffect above will trigger fetchIssues({}) automatically
  };

  const hasActiveFilters =
    search.vehicle_no || search.party_name || search.truck_owner;

  // ── Dropdown options from already-loaded issues ────────────────
  const truckOptions = [
    ...new Set(issues.map((i) => i.truck_no).filter(Boolean)),
  ];
  const truckOwnerOptions = [
    ...new Set(issues.map((i) => i.truck_owner).filter(Boolean)),
  ];
  const partyNameOptions = partyOptions.map((o) => o.name) || [];

  // ── Stats (computed from current API response) ─────────────────
  const totalRecoveryAmt = issues.reduce(
    (sum, i) => sum + (parseFloat(i.recovery_amount) || 0),
    0,
  );

  // ── Print ──────────────────────────────────────────────────────
  const handlePrint = () => {
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>Issue List</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #1e1b4b; }
        h2 { text-align: center; font-size: 18px; margin-bottom: 20px; color: #4f46e5; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #4f46e5; color: white; padding: 8px 10px; text-align: left; font-weight: 600; }
        td { padding: 7px 10px; border-bottom: 1px solid #e0e7ff; }
        tr:nth-child(even) td { background: #f5f3ff; }
      </style></head>
      <body><h2>Issue List</h2>
      <table><thead><tr><th>#</th><th>LR ID</th><th>Party Name</th><th>Truck No</th><th>Truck Owner</th><th>Description</th></tr></thead>
      <tbody>${issues
        .map(
          (issue, i) => `
        <tr><td>${i + 1}</td><td>${issue.freight_id || "—"}</td><td>${issue.party_name || "—"}</td>
        <td>${issue.truck_no || "—"}</td><td>${issue.truck_owner || "—"}</td><td>${issue.description || "—"}</td></tr>
      `,
        )
        .join("")}</tbody></table></body></html>
    `);
    w.document.close();
    w.print();
    w.close();
  };

  // ── Export Excel ───────────────────────────────────────────────
  const handleExportExcel = async () => {
    const XLSX = await import("xlsx-js-style");
    const headers = [
      "Sr No",
      "Party Name",
      "Truck No",
      "Truck Owner",
      "Recovery Amount",
      "Description",
    ];
    const rows = issues.map((issue, i) => [
      i + 1,
      issue.party_name || "—",
      issue.truck_no || "—",
      issue.truck_owner || "—",
      issue.recovery_amount || "—",
      issue.description || "—",
    ]);

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };
    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };
    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };
    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };
    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;
          const style = isNumber
            ? isEven
              ? numberStyle
              : numberStyleOdd
            : isEven
              ? cellStyleEven
              : cellStyleOdd;
          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      return { wch: Math.max(...allValues.map((v) => v.length)) + 4 };
    });
    ws["!rows"] = [{ hpt: 18 }];
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Issue List",
      Author: "Staff Management System",
      CreatedDate: new Date(),
    };
    XLSX.utils.book_append_sheet(wb, ws, "Issues");
    XLSX.writeFile(
      wb,
      `Issue_List_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -5, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stat Cards ── */}
      <Grid container spacing={1} sx={{ mb: 1.5, mt: 2 }}>
        {[
          {
            title: "Filtered Issues",
            value: issues.length,
            sub: `${totalIssuesCount} total unresolved`,
            color: T.warning,
            icon: WarningIcon,
          },
          {
            title: "Recovery Amount",
            value: formatCurrency(totalRecoveryAmt),
            sub: `across ${issues.length} issue(s)`,
            color: T.primary,
            icon: MoneyIcon,
          },
        ].map(({ title, value, sub, color, icon }) => (
          <Grid item xs={6} sm={6} key={title}>
            <StatCard
              title={title}
              value={value}
              subtext={sub}
              color={color}
              icon={icon}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Toolbar ── */}
      <Box sx={{ mt: 2, mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* Truck No */}
          <Grid item xs={12} sm={4} md="auto" sx={{ flex: { md: 1 } }}>
            <Autocomplete
              freeSolo
              options={truckOptions}
              value={search.vehicle_no}
              onInputChange={(_, v) =>
                setSearch((p) => ({ ...p, vehicle_no: v }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Truck No."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <TruckIcon sx={{ fontSize: 14, color: T.primary }} />
                        {loading && (
                          <CircularProgress
                            size={10}
                            sx={{ ml: 0.5, color: T.primary }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Party Name */}
          <Grid item xs={12} sm={4} md="auto" sx={{ flex: { md: 1 } }}>
            <Autocomplete
              freeSolo
              options={partyNameOptions}
              value={search.party_name}
              onInputChange={(_, v) =>
                setSearch((p) => ({ ...p, party_name: v }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Party Name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Truck Owner */}
          <Grid item xs={12} sm={4} md="auto" sx={{ flex: { md: 1 } }}>
            <Autocomplete
              freeSolo
              options={truckOwnerOptions}
              value={search.truck_owner}
              onInputChange={(_, v) =>
                setSearch((p) => ({ ...p, truck_owner: v }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Truck Owner"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Clear */}
          {hasActiveFilters && (
            <Grid item xs="auto">
              <Tooltip title="Clear Filters" arrow>
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ ...dangerBtn, height: 34, minWidth: 34, px: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
            </Grid>
          )}

          {/* Print */}
          <Grid item xs="auto">
            <Tooltip title="Print" arrow>
              <Button
                size="small"
                onClick={handlePrint}
                sx={{ ...iconBtn, height: 34 }}
              >
                <Print sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          {/* Export */}
          <Grid item xs="auto">
            <Tooltip title="Export Excel" arrow>
              <Button
                size="small"
                onClick={handleExportExcel}
                sx={{ ...iconBtn, height: 34 }}
              >
                <Download sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          {/* Refresh */}
          <Grid item xs="auto">
            <Tooltip title="Refresh" arrow>
              <Button
                size="small"
                onClick={() => fetchIssues(search)}
                sx={{ ...iconBtn, height: 34 }}
              >
                <Refresh sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Add Issue */}
          <Grid item xs={12} sm="auto" md="auto">
            <Button
              onClick={() => setOpenAddModal(true)}
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              sx={{ ...primaryBtn, height: 34, whiteSpace: "nowrap", px: 2 }}
            >
              Add Issue
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ── Table / Mobile ── */}
      {isMobile ? (
        loading && issues.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : issues.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.white,
              ...sq,
            }}
          >
            <WarningIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: T.text,
                mb: 0.5,
                fontSize: "0.9rem",
              }}
            >
              No issues found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}>
            {issues.map((issue, idx) => (
              <MobileIssueCard
                key={issue.issue_id || issue.id}
                issue={issue}
                idx={idx}
                expanded={expandedCardId === (issue.issue_id || issue.id)}
                onToggle={() => handleCardToggle(issue.issue_id || issue.id)}
                onSolve={(e, id) => {
                  setSolveAnchorEl(e.currentTarget);
                  setIssueToSolve(id);
                }}
              />
            ))}
          </Box>
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
          }}
        >
          <TableContainer
            sx={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 900,
                borderCollapse: "collapse",
                "& td, & th": {
                  py: 0,
                  px: 1,
                  fontSize: "0.75rem",
                  borderBottom: `1px solid #ccc`,
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                  textAlign: "center",
                },
                "& th": {
                  fontWeight: 700,
                  color: T.headerText,
                  bgcolor: `${T.headerBg} !important`,
                  py: 0.2,
                  height: 28,
                  fontSize: "0.8rem",
                  letterSpacing: "0.01em",
                  borderBottom: `1px solid #ccc`,
                  lineHeight: 1.2,
                },
                "& td": { height: 26 },
              }}
            >
              <TableHead>
                <TableRow>
                  {[
                    { l: "Solve", sticky: true, left: 0, w: 50 },
                    { l: "#", sticky: true, left: 50, w: 35 },
                    { l: "Party Name", w: 180 },
                    { l: "Truck No", w: 110 },
                    { l: "Truck Owner", w: 150 },
                    { l: "Recovery Amt", w: 120 },
                    { l: "Description", w: 300 },
                  ].map(({ l, sticky, left, w }) => (
                    <TableCell
                      key={l}
                      sx={{
                        minWidth: w,
                        bgcolor: `${T.headerBg} !important`,
                        color: `${T.headerText} !important`,
                        ...(sticky && { position: "sticky", left, zIndex: 12 }),
                      }}
                    >
                      {l}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && issues.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ textAlign: "center", py: 6, border: "none" }}
                    >
                      <CircularProgress sx={{ color: T.primary }} size={32} />
                    </TableCell>
                  </TableRow>
                ) : issues.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ textAlign: "center", py: 6, border: "none" }}
                    >
                      <WarningIcon
                        sx={{
                          fontSize: 40,
                          color: T.border,
                          mb: 1,
                          display: "block",
                          mx: "auto",
                        }}
                      />
                      <Typography
                        sx={{
                          color: T.textSub,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        No issues found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  issues.map((issue, idx) => {
                    const isEven = idx % 2 === 0;
                    const rowBg = isEven ? T.baseRow : T.altRow;
                    const stickyCell = {
                      bgcolor: `${rowBg} !important`,
                      zIndex: 5,
                    };

                    return (
                      <TableRow
                        key={issue.issue_id || issue.id}
                        sx={{
                          "& > td": {
                            bgcolor: rowBg,
                            transition: "background-color 0.12s",
                          },
                          "&:hover > td": {
                            bgcolor: `${T.rowHover} !important`,
                          },
                        }}
                      >
                        {/* Solve checkbox */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            ...stickyCell,
                            p: "2px 6px",
                          }}
                        >
                          <Checkbox
                            size="small"
                            onClick={(e) => {
                              setSolveAnchorEl(e.currentTarget);
                              setIssueToSolve(issue.issue_id);
                            }}
                            sx={{
                              p: 0.5,
                              color: T.primary,
                              "&.Mui-checked": { color: T.primary },
                            }}
                          />
                        </TableCell>

                        {/* # */}
                        <TableCell
                          sx={{ position: "sticky", left: 50, ...stickyCell }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: T.textMuted,
                            }}
                          >
                            {idx + 1}
                          </Typography>
                        </TableCell>

                        {/* Party Name */}
                        <TableCell>
                          <Tooltip title={issue.party_name || ""} arrow>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                maxWidth: 160,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {issue.party_name || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* Truck No */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: T.primary,
                              fontSize: "0.75rem",
                            }}
                          >
                            {issue.truck_no || "—"}
                          </Typography>
                        </TableCell>

                        {/* Truck Owner */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: T.text,
                              maxWidth: 130,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {issue.truck_owner || "—"}
                          </Typography>
                        </TableCell>

                        {/* Recovery Amount */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: T.success,
                            }}
                          >
                            {issue.recovery_amount
                              ? formatCurrency(issue.recovery_amount)
                              : "—"}
                          </Typography>
                        </TableCell>

                        {/* Description */}
                        <TableCell>
                          <Tooltip
                            title={issue.description || "No description"}
                            arrow
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: T.text,
                                maxWidth: 280,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {issue.description || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Add Issue Dialog ── */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
            minHeight: "85vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogHeader
          title="Add New Issue"
          onClose={() => setOpenAddModal(false)}
        />
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <IssueForm
            setIssues={setIssues}
            setOpenAddModal={setOpenAddModal}
            setSnackbar={setSnackbar}
            onSuccess={() => fetchIssues(search)} // ← respects active filters
          />
        </DialogContent>
      </Dialog>

      {/* ── Solve Popover ── */}
      <SolvePopover
        anchorEl={solveAnchorEl}
        onClose={() => {
          setSolveAnchorEl(null);
          setIssueToSolve(null);
        }}
        onConfirm={() => handleSolve(issueToSolve)}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            ...sq,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontWeight: 600,
            fontSize: "0.82rem",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
